import type {
  CustomSeriesRenderItemAPI,
  CustomSeriesRenderItemParams,
  EChartsOption,
} from "echarts";
import type {
  ChartThresholdSeverity,
  EChartsThemeTokens,
  EChartsThresholdStyle,
} from "@/shared/charts/theme/create-echarts-theme";
import type {
  MultiLineChartConfig,
  MultiLineChartPoint,
  MultiLineChartThreshold,
} from "@/shared/charts/components/multi-line-chart/types";
import { formatTimeAxisLabel } from "@/shared/charts/utils/format-time-axis-label";

function formatValue(value: number | null, config: MultiLineChartConfig): string {
  if (value == null) return "Unavailable";
  return config.valueFormatter?.(value) ?? value.toLocaleString("en-GB");
}

function getSeriesValue(point: MultiLineChartPoint, seriesId: string): number | null {
  for (const [id, value] of Object.entries(point.values)) {
    if (id === seriesId) return value;
  }

  return null;
}

function getSeriesMaxValue(data: MultiLineChartPoint[], seriesId: string): number | null {
  let max: number | null = null;
  for (const point of data) {
    const value = getSeriesValue(point, seriesId);
    if (value == null) continue;
    if (max == null || value > max) max = value;
  }

  return max;
}

interface MarkLineEntry {
  name?: string;
  yAxis: number;
  lineStyle: { color: string; width: number; type: "solid" | "dashed" };
  label: {
    show: boolean;
    formatter: string;
    position: "insideStartTop" | "insideEndTop" | "end";
    color: string;
    backgroundColor?: string;
    padding: [number, number];
    borderRadius?: number;
    fontFamily: string;
    fontSize: number;
    fontWeight: number;
  };
}

function getTimestamp(point: MultiLineChartPoint | undefined): number | null {
  if (point?.timestamp == null) return null;
  const timestamp = new Date(point.timestamp).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
}

function getTimeMissingDataRanges(
  data: MultiLineChartPoint[],
  series: MultiLineChartConfig["series"],
  axisRange: { min: number; max: number } | undefined
): [{ xAxis: number }, { xAxis: number }][] {
  let lastObservedIndex = -1;
  data.forEach((point, index) => {
    if (series.every((chartSeries) => getSeriesValue(point, chartSeries.id) != null)) {
      lastObservedIndex = index;
    }
  });
  const dataThroughLastObservation = data.slice(0, lastObservedIndex + 1);
  const ranges: [{ xAxis: number }, { xAxis: number }][] = [];
  let rangeStart: number | null = null;

  for (let index = 0; index <= dataThroughLastObservation.length; index += 1) {
    const point = dataThroughLastObservation.at(index);
    const isMissing =
      point != null && series.some((chartSeries) => getSeriesValue(point, chartSeries.id) == null);

    if (isMissing && rangeStart == null) rangeStart = index;
    if (!isMissing && rangeStart != null) {
      const startPoint = data.at(rangeStart);
      const endPoint = data.at(index - 1);
      const startTimestamp = startPoint == null ? null : getTimestamp(startPoint);
      const endTimestamp = endPoint == null ? null : getTimestamp(endPoint);
      const previousTimestamp =
        rangeStart > 0 ? getTimestamp(dataThroughLastObservation.at(rangeStart - 1)) : null;
      const nextTimestamp =
        index < dataThroughLastObservation.length
          ? getTimestamp(dataThroughLastObservation.at(index))
          : null;

      if (startTimestamp != null && endTimestamp != null) {
        const leftBoundary =
          previousTimestamp == null
            ? (axisRange?.min ??
              startTimestamp -
                ((nextTimestamp ?? startTimestamp + 60 * 60 * 1000) - startTimestamp) / 2)
            : (previousTimestamp + startTimestamp) / 2;
        const rightBoundary =
          nextTimestamp == null
            ? (axisRange?.max ??
              endTimestamp +
                (endTimestamp - (previousTimestamp ?? endTimestamp - 60 * 60 * 1000)) / 2)
            : (endTimestamp + nextTimestamp) / 2;
        ranges.push([{ xAxis: leftBoundary }, { xAxis: rightBoundary }]);
      }
      rangeStart = null;
    }
  }

  if (axisRange != null && data.length > 0) {
    const firstPoint = dataThroughLastObservation[0];
    const firstTimestamp = getTimestamp(firstPoint);
    const firstPointIsObserved =
      firstPoint != null &&
      series.every((chartSeries) => getSeriesValue(firstPoint, chartSeries.id) != null);
    if (firstPointIsObserved && firstTimestamp != null && firstTimestamp > axisRange.min) {
      const nextTimestamp = getTimestamp(dataThroughLastObservation[1]);
      const boundary =
        nextTimestamp == null ? firstTimestamp : (firstTimestamp + nextTimestamp) / 2;
      ranges.push([{ xAxis: axisRange.min }, { xAxis: Math.min(boundary, axisRange.max) }]);
    }
  }

  return ranges;
}

function getMissingDataRanges(
  data: MultiLineChartPoint[],
  series: MultiLineChartConfig["series"],
  isTimeAxis: boolean,
  axisRange: { min: number; max: number } | undefined
): [{ xAxis: number }, { xAxis: number }][] {
  if (isTimeAxis) return getTimeMissingDataRanges(data, series, axisRange);

  const ranges: [{ xAxis: number }, { xAxis: number }][] = [];
  let rangeStart: number | null = null;

  for (let index = 0; index <= data.length; index += 1) {
    const point = data.at(index);
    const isMissing =
      point != null && series.some((chartSeries) => getSeriesValue(point, chartSeries.id) == null);

    if (isMissing && rangeStart == null) rangeStart = index;
    if (!isMissing && rangeStart != null) {
      ranges.push([{ xAxis: rangeStart - 0.5 }, { xAxis: index - 0.5 }]);
      rangeStart = null;
    }
  }

  return ranges;
}

function getThresholdGridRight(config: MultiLineChartConfig, thresholdsVisible: boolean): number {
  if (!thresholdsVisible || config.thresholds == null || config.thresholds.length === 0) return 28;

  const longestLabel = Math.max(
    0,
    ...(config.thresholds?.map((threshold) => threshold.label.length) ?? [])
  );

  return Math.max(8, longestLabel * 7 + 8);
}

function getSeriesColor(
  series: MultiLineChartConfig["series"][number],
  index: number,
  tokens: EChartsThemeTokens
): string {
  return (
    series.color ??
    tokens.categoricalPalette.at(index % tokens.categoricalPalette.length) ??
    tokens.primarySeriesColor
  );
}

type ThresholdStyleKey = "barColor" | "lineColor" | "barHoverColor";

function getThresholdStyle(
  tokens: EChartsThemeTokens,
  severity: ChartThresholdSeverity
): EChartsThresholdStyle {
  switch (severity) {
    case "normal":
      return tokens.threshold.normal;
    case "warning":
      return tokens.threshold.warning;
    case "medium":
      return tokens.threshold.medium;
    case "high":
      return tokens.threshold.high;
    case "critical":
      return tokens.threshold.critical;
    case "moderate":
      return tokens.threshold.moderate;
    case "long":
      return tokens.threshold.long;
  }
}

function getThresholdStyleColor(style: EChartsThresholdStyle, key: ThresholdStyleKey): string {
  switch (key) {
    case "barColor":
      return style.barColor;
    case "lineColor":
      return style.lineColor;
    case "barHoverColor":
      return style.barHoverColor;
  }
}

interface ThresholdRangePiece {
  gt?: number;
  lte?: number;
  color: string;
}

function getThresholdRangePieces(
  thresholds: MultiLineChartThreshold[] | undefined,
  series: MultiLineChartConfig["series"][number],
  staticColor: string,
  tokens: EChartsThemeTokens
): ThresholdRangePiece[] {
  if (thresholds == null || thresholds.length === 0) return [];

  const sortedThresholds = [...thresholds].sort((a, b) => a.value - b.value);
  const firstThreshold = sortedThresholds.at(0);
  if (firstThreshold == null) return [];

  if (series.thresholdColoring === "severity") {
    const colorKey = series.type === "bar" ? "barColor" : "lineColor";
    return [
      {
        lte: firstThreshold.value,
        color: getThresholdStyleColor(getThresholdStyle(tokens, "normal"), colorKey),
      },
      ...sortedThresholds.map((threshold, index) => {
        const next = sortedThresholds.at(index + 1);
        const severity = threshold.severity ?? "normal";
        return {
          gt: threshold.value,
          ...(next == null ? {} : { lte: next.value }),
          color: getThresholdStyleColor(getThresholdStyle(tokens, severity), colorKey),
        };
      }),
    ];
  }

  return [
    { lte: firstThreshold.value, color: staticColor },
    ...sortedThresholds.map((threshold, index) => {
      const next = sortedThresholds.at(index + 1);
      return {
        gt: threshold.value,
        ...(next == null ? {} : { lte: next.value }),
        color: tokens.getThresholdSeriesColor(threshold.color ?? staticColor),
      };
    }),
  ];
}

function getThresholdSeverity(
  value: number,
  thresholds: MultiLineChartThreshold[]
): ChartThresholdSeverity {
  let severity: ChartThresholdSeverity = "normal";
  for (const threshold of [...thresholds].sort((a, b) => a.value - b.value)) {
    if (value > threshold.value) severity = threshold.severity ?? severity;
  }

  return severity;
}

function getBarSeverityStyle(
  value: number | null,
  thresholds: MultiLineChartThreshold[] | undefined,
  tokens: EChartsThemeTokens
): { color: string; emphasis: { itemStyle: { color: string } } } | undefined {
  if (value == null || thresholds == null || thresholds.length === 0) return undefined;

  const severity = getThresholdSeverity(value, thresholds);
  return {
    color: getThresholdStyle(tokens, severity).barColor,
    emphasis: { itemStyle: { color: getThresholdStyle(tokens, severity).barHoverColor } },
  };
}

export function createMultiLineChartOption(
  data: MultiLineChartPoint[],
  config: MultiLineChartConfig,
  tokens: EChartsThemeTokens
): EChartsOption {
  const colors = config.series.map((series, index) => getSeriesColor(series, index, tokens));
  const isTimeAxis = data.length > 0 && data.every((point) => getTimestamp(point) != null);
  const missingDataRanges = getMissingDataRanges(
    data,
    config.series,
    isTimeAxis,
    config.xAxisRange
  );
  const thresholdsVisible = config.thresholdsVisible !== false;
  const thresholds = thresholdsVisible ? config.thresholds : undefined;
  const thresholdRangePieces = config.series.map((series, index) =>
    getThresholdRangePieces(
      thresholds,
      series,
      colors.at(index) ?? tokens.primarySeriesColor,
      tokens
    )
  );
  const thresholdRangeVisualMaps = config.series.flatMap((series, index) => {
    const pieces = thresholdRangePieces.at(index) ?? [];
    // Severity-coloured bars use per-item styles so emphasis can preserve their hue.
    const usesItemSeverityStyles = series.type === "bar" && series.thresholdColoring === "severity";
    return pieces.length === 0 || usesItemSeverityStyles
      ? []
      : [
          {
            type: "piecewise" as const,
            show: false,
            seriesIndex: index,
            dimension: isTimeAxis ? 1 : 0,
            pieces,
          },
        ];
  });

  const visibleSeriesIds = config.visibleSeriesIds;
  const isSeriesVisible = (series: MultiLineChartConfig["series"][number]) =>
    visibleSeriesIds == null || visibleSeriesIds.has(series.id);
  const hasVisibleSeries = config.series.some(isSeriesVisible);
  const thresholdMarkLineEntries: MarkLineEntry[] =
    thresholds?.map((threshold) => {
      const thresholdColor =
        threshold.severity == null
          ? (threshold.color ?? tokens.axisLineColor)
          : tokens.threshold[threshold.severity].referenceLineColor;
      return {
        name: threshold.label,
        yAxis: threshold.value,
        lineStyle: { color: thresholdColor, width: 1, type: "dashed" },
        label: {
          show: true,
          formatter: threshold.label,
          position: "end",
          color: thresholdColor,
          padding: [0, 4],
          fontFamily: tokens.fontFamily,
          fontSize: 11,
          fontWeight: 700,
        },
      };
    }) ?? [];

  return {
    animation: config.animation ?? true,
    aria: {
      enabled: true,
      label: {
        enabled: true,
        description: config.ariaDescription,
      },
    },
    backgroundColor: tokens.backgroundColor,
    color: colors,
    grid: {
      top: 44,
      // Reserve only the space needed by threshold labels positioned at the right edge of mark lines.
      right: getThresholdGridRight(config, thresholdsVisible),
      bottom: 42,
      left: 8,
      containLabel: true,
    },
    textStyle: {
      color: tokens.textColor,
      fontFamily: tokens.fontFamily,
    },
    legend: [
      {
        top: 8,
        left: "center",
        itemGap: 24,
        // A square legend viewport lets bar series retain their rectangular marker while
        // line-series use a deliberately thin path within the same space.
        itemWidth: 12,
        itemHeight: 12,
        textStyle: {
          color: tokens.mutedTextColor,
          fontFamily: tokens.fontFamily,
          fontSize: 12,
        },
        data: config.series.map((series) => ({
          name: series.label,
          icon: series.type === "bar" ? "rect" : "path://M0,5 L20,5 L20,7 L0,7 Z",
        })),
        selected: Object.fromEntries(
          config.series.map((series) => [series.label, isSeriesVisible(series)])
        ),
      },
    ],
    tooltip: {
      // Native ECharts axis tooltip: no custom `formatter`, so ECharts renders its
      // own marker + series name + value list per hovered data point.
      // `valueFormatter` only controls how each value is displayed.
      trigger: "axis",
      confine: true,
      backgroundColor: tokens.tooltipBackgroundColor,
      borderColor: tokens.tooltipBorderColor,
      borderWidth: 1,
      padding: [8, 10],
      textStyle: {
        color: tokens.textColor,
        fontFamily: tokens.fontFamily,
        fontSize: 12,
        lineHeight: 18,
      },
      axisPointer: {
        type: "line",
        lineStyle: {
          color: tokens.axisLineColor,
          width: 2,
        },
      },
      valueFormatter: (value) =>
        typeof value === "number" ? formatValue(value, config) : formatValue(null, config),
    },
    xAxis: {
      ...(isTimeAxis
        ? {
            type: "time" as const,
            boundaryGap: [0, 0],
            ...(config.xAxisRange == null ? {} : config.xAxisRange),
            axisLabel: {
              color: tokens.mutedTextColor,
              fontFamily: tokens.fontFamily,
              fontSize: 10,
              margin: 14,
              hideOverlap: true,
              formatter: (value: number) =>
                formatTimeAxisLabel(
                  value,
                  config.xAxisRange ?? {
                    min: getTimestamp(data[0]) ?? value,
                    max: getTimestamp(data.at(-1)) ?? value,
                  }
                ),
            },
          }
        : {
            type: "category" as const,
            boundaryGap: false,
            data: data.map((point) => point.x),
            axisLabel: {
              color: tokens.mutedTextColor,
              fontFamily: tokens.fontFamily,
              fontSize: 10,
              margin: 14,
            },
          }),
      axisLine: {
        lineStyle: {
          color: tokens.axisLineColor,
        },
      },
      axisTick: { show: false },
    },
    dataZoom: [
      {
        type: "inside",
        xAxisIndex: 0,
        filterMode: "none",
        zoomLock: true,
        zoomOnMouseWheel: false,
        moveOnMouseWheel: false,
        moveOnMouseMove: true,
      },
    ],
    // Always provide the collection so replaceMerge can remove a prior threshold visual map.
    visualMap: thresholdRangeVisualMaps,
    yAxis: {
      type: "value",
      min: 0,
      splitNumber: 4,
      axisLabel: {
        color: tokens.mutedTextColor,
        fontFamily: tokens.fontFamily,
        fontSize: 10,
        formatter: (value: number) =>
          config.valueFormatter?.(value) ?? value.toLocaleString("en-GB"),
      },
      splitLine: {
        lineStyle: {
          color: tokens.gridLineColor,
          width: 1,
        },
      },
    },
    series: [
      ...config.series.map((series, index) => {
        const color = colors.at(index) ?? tokens.primarySeriesColor;
        const seriesType: "line" | "bar" = series.type === "bar" ? "bar" : "line";
        const peakLine = config.peakLine;

        const markLineEntries: MarkLineEntry[] = [
          ...(peakLine != null && peakLine.seriesId === series.id
            ? (() => {
                const peakValue = getSeriesMaxValue(data, series.id);
                if (peakValue == null) return [];

                const peakColor =
                  series.thresholdColoring === "severity" && thresholds != null
                    ? tokens.threshold[getThresholdSeverity(peakValue, thresholds)].lineColor
                    : color;
                const entry: MarkLineEntry = {
                  yAxis: peakValue,
                  lineStyle: { color: peakColor, width: 2, type: "dashed" },
                  label: {
                    show: true,
                    formatter: peakLine.formatLabel(peakValue),
                    position: "insideEndTop",
                    color: tokens.highlightTextColor,
                    backgroundColor: peakColor,
                    padding: [4, 8],
                    borderRadius: 4,
                    fontFamily: tokens.fontFamily,
                    fontSize: 12,
                    fontWeight: 700,
                  },
                };
                return [entry];
              })()
            : []),
        ];

        const seriesData = data.map((point) => {
          const value = getSeriesValue(point, series.id);
          if (seriesType === "bar" && series.thresholdColoring === "severity") {
            const itemStyle = getBarSeverityStyle(value, thresholds, tokens);
            if (!isTimeAxis) return itemStyle == null ? value : { value, itemStyle };

            const timestamp = getTimestamp(point);
            if (timestamp == null) return null;
            // Retain missing values at their timestamp so axis tooltips report this bar series as
            // unavailable alongside any missing line series at the same point.
            return itemStyle == null
              ? [timestamp, value]
              : { value: [timestamp, value], itemStyle };
          }
          if (!isTimeAxis) return value;

          const timestamp = getTimestamp(point);
          return timestamp == null ? null : [timestamp, value];
        });
        const markLine =
          markLineEntries.length > 0
            ? {
                markLine: {
                  silent: true,
                  z: 10,
                  symbol: ["none", "none"],
                  data: markLineEntries,
                },
              }
            : {};

        const commonSeries = { id: series.id, name: series.label, data: seriesData };

        if (seriesType === "bar") {
          return {
            ...commonSeries,
            type: "bar" as const,
            barMaxWidth: 28,
            barMinHeight: 2,
            itemStyle: {
              ...(thresholdRangePieces.at(index)?.length === 0 ? { color } : {}),
              opacity: series.thresholdColoring === "severity" ? 0.82 : 0.9,
            },
            z: series.thresholdColoring === "severity" ? 2 : 1,
            ...markLine,
          };
        }

        return {
          ...commonSeries,
          type: "line" as const,
          smooth: 0.25,
          connectNulls: false,
          showSymbol: false,
          symbol: "circle",
          symbolSize: 6,
          lineStyle: {
            ...(series.thresholdColoring === "severity" ? {} : { color }),
            width: series.thresholdColoring === "severity" ? 3 : 2,
            type: series.lineStyle ?? "solid",
            ...(series.thresholdColoring === "severity"
              ? { shadowColor: tokens.backgroundColor, shadowBlur: 1 }
              : {}),
          },
          z: series.thresholdColoring === "severity" ? 3 : 2,
          ...(thresholdRangePieces.at(index)?.length === 0 ? { itemStyle: { color } } : {}),
          ...(series.areaColor == null ? {} : { areaStyle: { color: series.areaColor } }),
          ...markLine,
        };
      }),
      ...(thresholdMarkLineEntries.length > 0
        ? [
            {
              // ECharts owns mark lines through a series. Keeping this host out of the legend
              // prevents threshold visibility from following a selectable data series.
              id: "threshold-reference-lines",
              name: "",
              type: "line" as const,
              data: [],
              silent: true,
              tooltip: { show: false },
              z: 10,
              markLine: {
                silent: true,
                z: 10,
                symbol: ["none", "none"],
                data: thresholdMarkLineEntries,
              },
            },
          ]
        : []),
      ...(hasVisibleSeries && missingDataRanges.length > 0
        ? [
            {
              id: "missing-data-overlay",
              name: "",
              type: "custom" as const,
              coordinateSystem: "cartesian2d",
              silent: true,
              z: 0,
              tooltip: { show: false },
              // The overlay ranges are X coordinates only; excluding Y prevents them from
              // expanding the value axis with their end-coordinate values.
              encode: { x: [0, 1] },
              renderItem: (
                _params: CustomSeriesRenderItemParams,
                api: CustomSeriesRenderItemAPI
              ) => {
                const coordSys = _params.coordSys;
                if (
                  !("x" in coordSys) ||
                  !("y" in coordSys) ||
                  !("width" in coordSys) ||
                  !("height" in coordSys) ||
                  typeof coordSys.x !== "number" ||
                  typeof coordSys.y !== "number" ||
                  typeof coordSys.width !== "number" ||
                  typeof coordSys.height !== "number"
                ) {
                  return null;
                }

                const start = api.coord([api.value(0), 0]);
                const end = api.coord([api.value(1), 0]);
                const left = Math.max(coordSys.x, Math.min(start[0], end[0]));
                const right = Math.min(coordSys.x + coordSys.width, Math.max(start[0], end[0]));

                return {
                  type: "rect" as const,
                  shape: {
                    x: left,
                    y: coordSys.y,
                    width: Math.max(0, right - left),
                    height: coordSys.height,
                  },
                  style: api.style({
                    fill: tokens.missingDataColor ?? "rgba(0, 0, 0, 0.3)",
                  }),
                };
              },
              data: missingDataRanges.map(([start, end]) => [start.xAxis, end.xAxis]),
            },
          ]
        : []),
    ],
  };
}
